// Pure, framework-free core for the autosave-field primitive. ALL decision logic
// lives here so it can be unit-tested with jest (the repo has no React Testing
// Library — see jest.config.js testEnvironment "node"). The React binding
// (useAutosaveField) and the presentational SaveStatus are thin wrappers.

export type SaveStatus = "idle" | "saving" | "saved" | "error"

export interface AutosaveState<T> {
  /** Valor mostrado en el campo (puede ir adelante de `committed` mientras editás). */
  value: T
  /** Último valor que se sabe persistido. */
  committed: T
  status: SaveStatus
  error: string | null
}

export type AutosaveAction<T> =
  | { type: "EDIT"; value: T }
  | { type: "COMMIT_START" }
  | { type: "COMMIT_OK"; value: T }
  | { type: "COMMIT_FAIL"; message: string } // blur: conserva el valor tipeado
  | { type: "REVERT"; message: string } // immediate: vuelve a committed
  | { type: "VALIDATE_FAIL"; message: string }
  | { type: "LINGER_DONE" }
  | { type: "RESET"; value: T }

export function makeInitialState<T>(value: T): AutosaveState<T> {
  return { value, committed: value, status: "idle", error: null }
}

export function autosaveReducer<T>(
  state: AutosaveState<T>,
  action: AutosaveAction<T>,
): AutosaveState<T> {
  switch (action.type) {
    case "EDIT":
      return { ...state, value: action.value, status: "idle", error: null }
    case "COMMIT_START":
      return { ...state, status: "saving", error: null }
    case "COMMIT_OK":
      return { ...state, committed: action.value, status: "saved", error: null }
    case "COMMIT_FAIL":
      return { ...state, status: "error", error: action.message }
    case "REVERT":
      return { ...state, value: state.committed, status: "error", error: action.message }
    case "VALIDATE_FAIL":
      return { ...state, status: "error", error: action.message }
    case "LINGER_DONE":
      return state.status === "saved" ? { ...state, status: "idle" } : state
    case "RESET":
      return makeInitialState(action.value)
    default:
      return state
  }
}

export interface CommitDecision {
  shouldPersist: boolean
  validationError: string | null
  unchanged: boolean
}

/** Decide si persistir: salta si no cambió; bloquea si validación falla. PURO. */
export function decideCommit<T>(
  value: T,
  committed: T,
  validate?: (v: T) => string | null,
  equals: (a: T, b: T) => boolean = Object.is,
): CommitDecision {
  if (equals(value, committed)) {
    return { shouldPersist: false, validationError: null, unchanged: true }
  }
  const validationError = validate ? validate(value) : null
  if (validationError) {
    return { shouldPersist: false, validationError, unchanged: false }
  }
  return { shouldPersist: true, validationError: null, unchanged: false }
}

export type SaveStatusView =
  | { tone: "muted"; role: "status"; kind: "saving" }
  | { tone: "success"; role: "status"; kind: "saved" }
  | { tone: "error"; role: "alert"; kind: "error"; message: string | null }
  | null

/** Mapea status → qué mostrar (tono + rol a11y + kind). Sin texto (i18n vive en SaveStatus). PURO. */
export function describeSaveStatus(status: SaveStatus, error: string | null): SaveStatusView {
  switch (status) {
    case "saving":
      return { tone: "muted", role: "status", kind: "saving" }
    case "saved":
      return { tone: "success", role: "status", kind: "saved" }
    case "error":
      return { tone: "error", role: "alert", kind: "error", message: error }
    default:
      return null
  }
}

/**
 * Encaja un número en [min, max]. NaN cae al mín (o 0 si no hay mín). Sin límites,
 * devuelve el valor tal cual. Para campos numéricos que clampean on-blur antes de
 * persistir (ej. montos, tamaños de party). PURO.
 */
export function clampNumber(value: number, min?: number, max?: number): number {
  if (Number.isNaN(value)) return min ?? 0
  let out = value
  if (min != null && out < min) out = min
  if (max != null && out > max) out = max
  return out
}
